import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { Home, ArrowLeft, MessageSquare, Send } from 'lucide-react';

export default function MessagesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
      if (response.data.length > 0) {
        setSelectedConv((prev) => prev || response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch conversations');
    } finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/messages/${otherUserId}`);
      setMessages(response.data);
    } catch (error) { log.error('Failed to fetch messages'); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (selectedConv) fetchMessages(selectedConv.other_user_id);
  }, [selectedConv, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedConv.other_user_id,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedConv.other_user_id);
      fetchConversations();
    } catch (error) { toast.error('Failed to send message'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Home className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight mb-4 sm:mb-6">Messages</h1>

        {loading ? (
          <div className="text-center py-20">Loading...</div>
        ) : conversations.length === 0 ? (
          <Card className="p-20 text-center border-2 border-dashed">
            <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No Messages Yet</h3>
            <p className="text-muted-foreground">Contact landlords from property listings to start a conversation</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 h-[calc(100vh-200px)] md:h-[600px]">
            {/* Conversations list — hidden on mobile once a chat is open */}
            <Card className={`border border-border overflow-hidden ${selectedConv ? 'hidden md:block' : ''}`}>
              <div className="p-3 sm:p-4 border-b border-border font-medium text-sm sm:text-base">Conversations</div>
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100% - 52px)' }}>
                {conversations.map((conv, idx) => (
                  <div key={conv.conversation_id} data-testid={`conversation-${idx}`}
                    onClick={() => setSelectedConv(conv)}
                    className={`p-3 sm:p-4 cursor-pointer border-b border-border hover:bg-muted ${selectedConv?.conversation_id === conv.conversation_id ? 'bg-muted' : ''}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium truncate mr-2">{conv.other_user_name}</div>
                      {conv.unread_count > 0 && (
                        <span className="bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full shrink-0">{conv.unread_count}</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {conv.last_sent_by_employee && (
                        <span className="text-[10px] uppercase tracking-wide text-primary/70 font-semibold mr-1">Team ·</span>
                      )}
                      {conv.last_message}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Chat pane — hidden on mobile until a conversation is picked */}
            <Card className={`md:col-span-2 border border-border flex flex-col ${!selectedConv ? 'hidden md:flex' : ''}`}>
              {selectedConv ? (
                <>
                  <div className="p-3 sm:p-4 border-b border-border font-medium text-sm sm:text-base flex items-center gap-2">
                    <button
                      data-testid="message-back-btn"
                      onClick={() => setSelectedConv(null)}
                      className="md:hidden p-1 -ml-1 rounded hover:bg-muted"
                      aria-label="Back to conversations"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <span className="truncate">Chat with {selectedConv.other_user_name}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
                    {messages.map((msg, idx) => {
                      const workspaceId = user.role === 'employee' ? user.parent_landlord_id : user.id;
                      const isMine = msg.sender_id === user.id || (msg.on_behalf_of_landlord_id && msg.on_behalf_of_landlord_id === workspaceId);
                      const showTeamTag = msg.sent_by_employee;
                      return (
                        <div key={msg.id} data-testid={`message-${idx}`} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${isMine ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                            {showTeamTag && (
                              <div className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${isMine ? 'text-primary-foreground/80' : 'text-primary/70'}`}>
                                {isMine
                                  ? `${msg.sender_name} · Landlord Team`
                                  : `${msg.on_behalf_of_landlord_name || 'Landlord'} Team · ${msg.sender_name}`}
                              </div>
                            )}
                            <div className="text-sm break-words">{msg.content}</div>
                            <div className={`text-xs mt-1 ${isMine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="p-3 sm:p-4 border-t border-border flex gap-2">
                    <Input data-testid="message-input" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()} placeholder="Type a message..." />
                    <Button data-testid="message-send-btn" onClick={handleSend} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-8 text-center">Select a conversation</div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
